import { useState, useEffect } from "react";
import styles from "./AllNotificationsPage.module.css";
import { FiSearch } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";
import SchedulesPage from "./SchedulesPage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const AUDIENCE_LABELS = {
  all: "Everyone",
  all_users: "All Users",
  all_traders: "All Traders",
  free: "Free Users",
  paid: "Paid Users",
  per_plan: "Per Plan",
  per_user: "Specific Users",
  per_trader: "Specific Traders",
  system: "System",
};

const AllNotificationsPage = () => {
  const { setIsCreateNotificationScreen } = useDashboardStore();
  const [view, setView] = useState("sent"); // sent | scheduled
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);

  const fetch = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(ENDPOINTS.GET_NOTIFICATIONS_ADMIN);
      if (data.success) setItems(data.notifications || []);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
      toast.error("Failed to fetch notifications");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const handleResend = (n) => {
    if (n.source === "system") {
      toast.error("System notifications cannot be resent");
      return;
    }
    confirmAlert({
      title: "Resend notification",
      message: `Resend "${n.title}" to the original audience now?`,
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              setResendingId(n.id);
              const { data } = await api.post(
                ENDPOINTS.RESEND_NOTIFICATION_ADMIN(n.id),
              );
              if (data.success) {
                toast.success(
                  `Resent to ${data.recipient_count} recipient${
                    data.recipient_count === 1 ? "" : "s"
                  }`,
                );
                fetch();
              } else {
                toast.error(data.message || "Failed to resend");
              }
            } catch (err) {
              toast.error(
                err.response?.data?.message || "Failed to resend notification",
              );
            } finally {
              setResendingId(null);
            }
          },
        },
        { label: "No", onClick: () => {} },
      ],
    });
  };

  const handleDelete = (n) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Delete this notification for all recipients?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_NOTIFICATION_ADMIN(n.id),
              );
              if (data.success) {
                toast.success("Notification deleted");
                fetch();
              }
            } catch (err) {
              toast.error("Failed to delete");
            }
          },
        },
        { label: "No", onClick: () => {} },
      ],
    });
  };

  const filtered = items.filter((n) => {
    const q = search.toLowerCase();
    return (
      n.title?.toLowerCase().includes(q) ||
      n.body?.toLowerCase().includes(q)
    );
  });

  const tabs = (
    <div style={{ display: "flex", gap: 8 }}>
      <button
        className={styles.addButton}
        style={{
          opacity: view === "sent" ? 1 : 0.55,
        }}
        onClick={() => setView("sent")}
      >
        Sent
      </button>
      <button
        className={styles.addButton}
        style={{
          opacity: view === "scheduled" ? 1 : 0.55,
        }}
        onClick={() => setView("scheduled")}
      >
        Scheduled
      </button>
    </div>
  );

  if (view === "scheduled") {
    return (
      <div className={styles.container}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Notifications</h2>
          <div className={styles.appBarRight}>
            {tabs}
            <button
              className={styles.addButton}
              onClick={() => setIsCreateNotificationScreen()}
            >
              + New Schedule
            </button>
          </div>
        </div>
        <SchedulesPage />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Notifications</h2>
          <div className={styles.appBarRight}>{tabs}</div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>Notifications</h2>
        <div className={styles.appBarRight}>
          {tabs}
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search by title or body..."
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FiSearch className={styles.searchIcon} />
          </div>
          <button
            className={styles.addButton}
            onClick={() => setIsCreateNotificationScreen()}
          >
            + Send Notification
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Sent</th>
              <th>Source</th>
              <th>Title</th>
              <th>Body</th>
              <th>Sub Body</th>
              <th>Audience</th>
              <th>Link</th>
              <th>Recipients</th>
              <th>Read</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={11} className={styles.emptyCell}>
                  No notifications found.
                </td>
              </tr>
            ) : (
              filtered.map((n) => (
                <tr key={n.id}>
                  <td>{dayjs.utc(n.sent_at).local().format("DD-MM-YYYY HH:mm")}</td>
                  <td>
                    <span
                      className={`${styles.sourceBadge} ${
                        n.source === "system"
                          ? styles.sourceSystem
                          : styles.sourceAdmin
                      }`}
                    >
                      {n.source}
                    </span>
                  </td>
                  <td className={styles.contentCell}>{n.title}</td>
                  <td className={styles.contentCell}>{n.body}</td>
                  <td className={styles.contentCell}>
                    {n.sub_body ? n.sub_body : <span className={styles.lifetime}>—</span>}
                  </td>
                  <td>{AUDIENCE_LABELS[n.audience_type] || n.audience_type}</td>
                  <td className={styles.contentCell}>
                    {n.link_url ? (
                      <a
                        href={n.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkText}
                      >
                        {n.link_url}
                      </a>
                    ) : (
                      <span className={styles.lifetime}>—</span>
                    )}
                  </td>
                  <td>{n.recipient_count}</td>
                  <td>
                    {n.read_count}/{n.recipient_count}
                  </td>
                  <td>
                    {n.expires_at ? (
                      dayjs.utc(n.expires_at).local().format("DD-MM-YYYY HH:mm")
                    ) : (
                      <span className={styles.lifetime}>Never</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.actionsCell}>
                      {n.source !== "system" && (
                        <span
                          className={`${styles.resend} ${
                            resendingId === n.id ? styles.disabled : ""
                          }`}
                          onClick={() =>
                            resendingId === n.id ? null : handleResend(n)
                          }
                          title="Resend the same notification to the original audience"
                        >
                          {resendingId === n.id ? "Resending…" : "Resend"}
                        </span>
                      )}
                      <span
                        className={styles.delete}
                        onClick={() => handleDelete(n)}
                      >
                        Delete
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllNotificationsPage;
